import { Validators } from '@biteinc/common';
import { allEnumValues, ComboUpsellType, FulfillmentMethod, ItemHiddenState, ItemTaxationStatusHelper, MenuItemCategory, MenuItemDisplayStyle, MenuItemSaleUnit, UserRight, } from '@biteinc/enums';
import { SyncableMenuModelSchema } from './syncable_menu_model_schema.js';
export const menuItemSchema = {
    displayName: 'menu item',
    type: 'object',
    fields: {
        ...SyncableMenuModelSchema.fields,
        isMod: {
            type: 'bool',
            isHidden: true,
            isPermanent: true,
            isReadOnly: true,
            required: true,
        },
        constituentItems: {
            type: 'array',
            elementType: 'object',
            // Dropping the minCount because constituent items can get removed during the lifetime
            // of the compound item (e.g. during sync). Otherwise, model inflation will fail.
            displayName: 'Constituent Items',
            placeholder: 'Select Constituent Items',
            fields: {
                _id: {
                    type: 'mongoId',
                    required: true,
                },
            },
            writeRight: UserRight.ManageMenu,
        },
        invalidReason: {
            type: 'shortString',
            displayName: 'Invalid Reason',
            placeholder: 'Compound Item is Valid',
            isReadOnly: true,
            isNullable: true,
        },
        name: {
            type: 'shortString',
            canHaveLocalizationOverrides: true,
            writeRight: UserRight.ManageMenu,
        },
        internalName: {
            type: 'shortString',
            displayName: 'Internal Name',
            collapse: true,
            tooltip: {
                text: 'A name used to identify this item internally. Not seen by guests.',
            },
            writeRight: UserRight.ManageMenu,
        },
        posName: {
            type: 'shortString',
            displayName: 'POS Name',
            isReadOnly: true,
            tooltip: {
                text: 'This is the default display name. The field “Display Name” will replace this name on the kiosk.',
            },
        },
        posInternalName: {
            type: 'keyString',
            isReadOnly: true,
            isHidden: true,
        },
        fulfillmentName: {
            type: 'shortString',
            displayName: 'Fulfillment Name',
            tooltip: {
                text: 'This is the name that will be displayed on the KDS for fulfillment integrations.',
            },
        },
        description: {
            type: 'longString',
            canHaveLocalizationOverrides: true,
            writeRight: UserRight.ManageMenu,
        },
        posDescription: {
            type: 'longString',
            displayName: 'POS Description',
            isReadOnly: true,
            tooltip: {
                text: 'This is the default display description. The field “Display Description” will replace this description on the kiosk.',
            },
        },
        vendorId: {
            type: 'mongoId',
            displayName: 'Vendor',
            ui: 'dropdown',
            required: true,
            writeRight: UserRight.ManageMenu,
        },
        story: {
            type: 'longString',
            canHaveLocalizationOverrides: true,
            tooltip: {
                text: 'Additional descriptive text shown only when the item is clicked.',
            },
            writeRight: UserRight.ManageMenu,
        },
        category: {
            type: 'int',
            values: allEnumValues(MenuItemCategory),
            ui: 'radiolist',
            required: true,
            writeRight: UserRight.ManageMenu,
        },
        taxationStatus: {
            type: 'int',
            values: ItemTaxationStatusHelper.allValuesForMenuItems(),
            displayName: 'Taxation Status',
            ui: 'dropdown',
            required: true,
            writeRight: UserRight.ManageMenu,
        },
        taxProfileId: {
            type: 'mongoId',
            displayName: 'Tax Profile',
            ui: 'dropdown',
            writeRight: UserRight.ManageMenu,
        },
        i9nTaxProfileId: {
            type: 'keyString',
            isReadOnly: true,
            isHidden: true,
        },
        badges: {
            displayName: 'Badges',
            type: 'array',
            elementType: 'object',
            fields: {
                _id: {
                    type: 'mongoId',
                    required: true,
                },
                i9nId: {
                    type: 'keyString',
                },
            },
            ui: 'checklist',
            canCreateNew: true,
            writeRight: UserRight.ManageMenu,
        },
        i9nAvailability: {
            type: 'object',
            displayName: 'Availability',
            subtype: 'MenuItemAvailability',
            isReadOnly: true,
            writeRight: UserRight.ManageMenu,
        },
        i9nUnavailableFulfillmentMethods: {
            type: 'array',
            elementType: 'int',
            elementValues: allEnumValues(FulfillmentMethod),
            isReadOnly: true,
            displayName: 'Unavailable Dining Options',
            ui: 'table',
        },
        images: {
            type: 'array',
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            maxCount: 5,
            keepAspectRatio: 'contain',
            size: {
                width: 600,
                height: 400,
            },
            jpgSize: {
                width: 1200,
                height: 800,
            },
            canHaveLocalizationOverrides: true,
            writeRight: UserRight.ManageMenu,
        },
        thumbnailImages: {
            type: 'array',
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            maxCount: 5,
            keepAspectRatio: 'contain',
            size: {
                width: 300,
                height: 200,
            },
            jpgSize: {
                width: 300,
                height: 200,
            },
            canHaveLocalizationOverrides: true,
            writeRight: UserRight.ManageMenu,
        },
        deemphasizeButtonImage: {
            displayName: 'Deemphasize Mod Groups Button Image',
            type: 'array',
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            maxCount: 1,
            keepAspectRatio: 'fit-width',
            size: {
                width: 900,
                height: 200,
            },
            jpgSize: {
                width: 1200,
                height: 800,
            },
            canHaveLocalizationOverrides: true,
            writeRight: UserRight.ManageMenu,
            tooltip: {
                text: 'This image will be used for the deemphasized modifier groups button on the kiosk.',
            },
        },
        displayStyle: {
            type: 'int',
            values: allEnumValues(MenuItemDisplayStyle),
            displayName: 'Display Style',
            ui: 'dropdown',
            required: true,
            writeRight: UserRight.ManageMenu,
        },
        useFullScreenCustomizationFlow: {
            displayName: 'Use Full Screen Customization Flow',
            type: 'bool',
            writeRight: UserRight.ManageMenu,
        },
        activeTimings: {
            type: 'array',
            elementType: 'object',
            collapse: true,
            displayName: 'Show Only During',
            placeholder: 'Select a Timing',
            warningHtml: 'This menu item will only be shown on the menu during the times you selected. If nothing is selected, this menu item will always be shown.',
            requiresTimings: true,
            fields: {
                _id: {
                    type: 'mongoId',
                    required: true,
                },
            },
            writeRight: UserRight.ManageMenu,
        },
        comboUpsell: {
            type: 'object',
            displayName: 'Combo Upsell',
            isNullable: true,
            defaultValue: null,
            fields: {
                type: {
                    displayName: 'Type',
                    type: 'stringEnum',
                    enum: ComboUpsellType,
                    ui: 'dropdown',
                    writeRight: UserRight.ManageMenu,
                    required: true,
                },
                comboItemId: {
                    type: 'mongoId',
                    displayName: 'Upsell Item',
                    ui: 'dropdown',
                    conditions: [
                        {
                            field: 'type',
                            values: [ComboUpsellType.MenuItem],
                        },
                    ],
                    writeRight: UserRight.ManageMenu,
                    required: true,
                },
                comboImage: {
                    displayName: 'Combo Image to override the item image',
                    type: 'array',
                    elementType: 'object',
                    elementSubtype: 'Image',
                    elementUniqueIdFieldName: 'origUrl',
                    maxCount: 1,
                    // 4:3 aspect ratio
                    keepAspectRatio: 'contain',
                    size: {
                        width: 900,
                        height: 675,
                    },
                    canHaveLocalizationOverrides: true,
                    conditions: [
                        {
                            field: 'type',
                            values: [ComboUpsellType.ComboBuilderModGroup],
                        },
                    ],
                    writeRight: UserRight.ManageMenu,
                },
            },
        },
        upsellModifierItemId: {
            type: 'mongoId',
            displayName: 'Upsell Modifier',
            ui: 'dropdown',
            writeRight: UserRight.ManageMenu,
            isNullable: true,
            isHidden: true,
            tooltip: {
                text: 'The recommended modifier for the item. The modifier must be assigned to this item and can only have one price option. The modifier may have nested modifiers.',
            },
        },
        priceOptionCategory: {
            type: 'shortString',
            displayName: 'Option Type (e.g. size, flavor)',
            canHaveLocalizationOverrides: true,
            writeRight: UserRight.ManageMenu,
        },
        buildYourOwnBaseImage: {
            type: 'array',
            displayName: 'Base Image for Build Your Own Item',
            writeRight: UserRight.ManageMenu,
            collapse: true,
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            maxCount: 5,
            size: {
                width: 600,
                height: 400,
            },
            jpgSize: {
                width: 1200,
                height: 800,
            },
            tooltip: {
                text: 'If an image is set, this item will use the Build Your Own Item UI.',
            },
        },
        assortedBaseImage: {
            type: 'array',
            displayName: 'Base Image for Build Your Own Assorted Box Item',
            writeRight: UserRight.ManageMenu,
            collapse: true,
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            maxCount: 1,
            keepAspectRatio: 'contain',
            size: {
                width: 600,
                height: 400,
            },
            jpgSize: {
                width: 1200,
                height: 800,
            },
            tooltip: {
                text: 'The assorted box feature will be activated if this image is provided and there is an appropriate mod group of 3, 4, 6, 8, or 12 mods',
            },
        },
        isRetail: {
            displayName: 'Retail Item',
            type: 'bool',
            tooltip: {
                text: 'Marks an item as retail/self-service which can affect behavior like pick up instructions.',
            },
            writeRight: UserRight.ManageMenu,
        },
        denormalizeModGroups: {
            type: 'bool',
            displayName: 'Create specific mod groups for this menu item',
            writeRight: UserRight.ManageMenuAdvanced,
            tooltip: {
                text: 'The POS integration needs to be synced for this change to take effect. Brink/Olo/Omnivore/Qu/Simphony/Toast only!',
                isWarning: true,
            },
        },
        priceOptions: {
            type: 'array',
            elementType: 'object',
            ui: 'accordion',
            uiSubtype: 'PriceOptions',
            required: true,
            minCount: 1,
            fields: {
                _id: {
                    type: 'mongoId',
                    isHidden: true,
                },
                i9nId: {
                    type: 'keyString',
                    // not readOnly because of compound items
                    isHidden: true,
                },
                posId: {
                    type: 'keyString',
                    // not readOnly because of compound items
                    isHidden: true,
                },
                name: {
                    type: 'shortString',
                    displayName: 'Price Option Name',
                    canHaveLocalizationOverrides: true,
                    writeRight: UserRight.ManageMenu,
                },
                posName: {
                    type: 'shortString',
                    displayName: 'POS Name',
                    isPermanent: true,
                    isReadOnly: true,
                    tooltip: {
                        text: 'This is the default display name. The field “Price Option Name” will replace this name on the kiosk.',
                    },
                },
                description: {
                    type: 'shortString',
                    displayName: 'Price Option Description',
                    canHaveLocalizationOverrides: true,
                    writeRight: UserRight.ManageMenu,
                },
                posDescription: {
                    type: 'shortString',
                    displayName: 'POS Description',
                    isPermanent: true,
                    isReadOnly: true,
                    tooltip: {
                        text: 'This is the default display description. The field “Price Option Description” will replace this name on the kiosk.',
                    },
                },
                fulfillmentName: {
                    type: 'shortString',
                    displayName: 'Fulfillment Name',
                    tooltip: {
                        text: 'This is the name that will be displayed on the KDS for fulfillment integrations.',
                    },
                },
                price: {
                    type: 'price',
                    canBeNegative: true,
                    displayName: 'Default Price',
                    required: true,
                    writeRight: UserRight.ManageMenu,
                },
                barcode: {
                    type: 'keyString',
                    validationRegex: Validators.barcodeRegexString,
                    writeRight: UserRight.ManageMenu,
                },
                posBarcode: {
                    displayName: 'POS Barcode',
                    type: 'keyString',
                    isPermanent: true,
                    isReadOnly: true,
                },
                nutritionInfo: {
                    type: 'object',
                    displayName: 'Nutrition Info',
                    subtype: 'NutritionInfo',
                    writeRight: UserRight.ManageMenu,
                    defaultValue: null,
                },
                saleUnit: {
                    type: 'int',
                    values: allEnumValues(MenuItemSaleUnit),
                    displayName: 'Sale Unit',
                    ui: 'dropdown',
                    biteOnly: true,
                },
                selectedByDefault: {
                    type: 'bool',
                    displayName: 'Selected by Default',
                    writeRight: UserRight.ManageMenu,
                },
                i9nSelectedByDefault: {
                    type: 'bool',
                    displayName: 'POS Selected by Default',
                    isReadOnly: true,
                },
                hiddenState: {
                    type: 'int',
                    values: allEnumValues(ItemHiddenState),
                    isHidden: true,
                },
                i9nHiddenState: {
                    type: 'int',
                    values: allEnumValues(ItemHiddenState),
                    isHidden: true,
                    isReadOnly: true,
                },
                addonSetIds: {
                    type: 'array',
                    elementType: 'mongoId',
                    displayName: 'Modifier Groups',
                    ui: 'table',
                    placeholder: 'Select Modifier Group',
                    collapse: true,
                    writeRight: UserRight.ManageMenu,
                },
                i9nAddonSetIds: {
                    type: 'array',
                    elementType: 'keyString',
                    isReadOnly: true,
                    isHidden: true,
                },
                floatingModGroupIds: {
                    type: 'array',
                    elementType: 'mongoId',
                    displayName: 'Floating Mod Groups',
                    ui: 'table',
                    placeholder: 'Select Floating Modifier Group',
                    collapse: true,
                    writeRight: UserRight.ManageMenu,
                },
                archivedAt: {
                    type: 'timestamp',
                    isReadOnly: true,
                    isHidden: true,
                },
                images: {
                    type: 'array',
                    elementType: 'object',
                    elementSubtype: 'Image',
                    elementUniqueIdFieldName: 'origUrl',
                    maxCount: 5,
                    keepAspectRatio: 'contain',
                    size: {
                        width: 600,
                        height: 400,
                    },
                    jpgSize: {
                        width: 1200,
                        height: 800,
                    },
                    canHaveLocalizationOverrides: true,
                },
            },
            writeRight: UserRight.ManageMenu,
        },
        scores: {
            type: 'object',
            isFreeform: true,
            isHidden: true,
            isReadOnly: true,
        },
        weightedScores: {
            type: 'object',
            isFreeform: true,
            isHidden: true,
            isReadOnly: true,
        },
        originalI9nData: {
            type: 'object',
            isFreeform: true,
            isHidden: true,
            isReadOnly: true,
        },
        variations: {
            type: 'array',
            elementType: 'mongoId',
            isHidden: true,
            isReadOnly: true,
        },
        variationSource: {
            type: 'object',
            isHidden: true,
            isReadOnly: true,
            fields: {
                i9nId: {
                    type: 'keyString',
                    required: true,
                },
                magicCopyKey: {
                    type: 'keyString',
                    required: true,
                },
                i9nIdSuffix: {
                    type: 'keyString',
                    required: true,
                },
                isRoot: {
                    type: 'bool',
                },
            },
        },
        recommendationLabel: {
            type: 'shortString',
            displayName: 'Custom Recommendation Label',
            canHaveLocalizationOverrides: true,
            writeRight: UserRight.ManageMenu,
        },
        instructions: {
            type: 'longString',
            displayName: 'Instructions to be printed on the receipt',
            canHaveLocalizationOverrides: true,
            writeRight: UserRight.ManageMenu,
        },
        prepTimeMinutes: {
            type: 'int',
            min: 0,
            max: 1440, // 24 hours
            displayName: 'Prep time (minutes)',
            writeRight: UserRight.ManageMenu,
            tooltip: {
                text: 'How long this item takes to prepare. When the location lead time source is set to menu item prep times, the order lead time is the longest prep time in the cart. Overrides posPrepTimeMinutes.',
            },
        },
        posPrepTimeMinutes: {
            type: 'int',
            min: 0,
            max: 1440, // 24 hours
            displayName: 'POS Prep time (minutes)',
            isPermanent: true,
            isReadOnly: true,
            tooltip: {
                text: 'The prep time synced from the POS. The field "Prep time (minutes)" replaces this value when set.',
            },
        },
    },
    searchFields: [
        'name',
        'posName',
        'internalName',
        'posInternalName',
        'fulfillmentName',
        'posId',
        'i9nId',
        'description',
        'posDescription',
        'story',
    ],
};
//# sourceMappingURL=menu_item_schema.js.map